import React, { Suspense } from 'react';
import type { ComponentType } from 'react';

import type { ExtensionComponentProps } from '@atlaskit/editor-common/extensions/extension-manifest';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import { resolveNativeEmbedEffectiveWidth } from '@atlaskit/native-embeds-common/initial-layout';
import { NativeEmbedInitialPlaceholder } from '@atlaskit/native-embeds-common/initial-placeholder';
import type { NativeEmbedParameters } from '@atlaskit/native-embeds-common/types';
import { getParameters } from '@atlaskit/native-embeds-common/utils';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';

import type NativeEmbedUIComponent from './ui';
import { isInlineMauiWidthEligible } from './ui/inline-maui-width';

type NativeEmbedExtensionParams = Parameters & NativeEmbedParameters;
export type NativeEmbedUILoaderProps = React.ComponentProps<typeof NativeEmbedUIComponent>;

const NativeEmbedUI = React.lazy<ComponentType<NativeEmbedUILoaderProps>>(() =>
	import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui').then(
		({ default: NativeEmbedUIComponent }) => ({
			default: NativeEmbedUIComponent,
		}),
	),
);

export const NativeEmbedLoadingPlaceholder = ({
	node,
}: ExtensionComponentProps<NativeEmbedExtensionParams>): React.JSX.Element => {
	const { parameters } = node;
	const { url, width } = getParameters(parameters);
	const manifest = url ? getNativeEmbedFacade().getManifestByUrl(url) : undefined;
	const effectiveWidth = resolveNativeEmbedEffectiveWidth({ manifest, width });
	const shouldFillInlineWidth =
		isInlineMauiWidthEligible({
			experienceId: manifest?.id,
			nodeType: node.type,
			width: effectiveWidth,
		}) && isExperimentEnabled('cc_maui_inline_embed_width_fix');

	return (
		<NativeEmbedInitialPlaceholder
			manifest={manifest}
			parameters={parameters}
			shouldFillInlineWidth={shouldFillInlineWidth}
		/>
	);
};

/**
 * Synchronous shell for the editor extension renderer.
 *
 * Editor's generic async extension loading state renders `null`, which causes the native embed block to
 * collapse before the real UI chunk arrives. This small shell keeps the full UI split into its existing
 * async chunk while providing a dimensionally accurate placeholder immediately.
 */
export default function NativeEmbedUILoader(props: NativeEmbedUILoaderProps): React.JSX.Element {
	return (
		<Suspense fallback={<NativeEmbedLoadingPlaceholder {...props} />}>
			<NativeEmbedUI {...props} />
		</Suspense>
	);
}
